import { useState } from "react";
import { useContractVersions, ContractVersion } from "@/hooks/useContractAnalytics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GitCompare, ArrowRight, Calendar, User, Mail, FileText } from "lucide-react";
import { format } from "date-fns";

interface ContractVersionComparisonProps {
  contractId: string;
}

const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  draft: "secondary",
  sent: "default",
  viewed: "outline",
  signed: "default",
  expired: "destructive",
};

interface ComparisonFieldProps {
  label: string;
  icon: React.ReactNode;
  leftValue: string | null | undefined;
  rightValue: string | null | undefined;
}

function ComparisonField({ label, icon, leftValue, rightValue }: ComparisonFieldProps) {
  const isDifferent = leftValue !== rightValue;
  const left = leftValue || "Not set";
  const right = rightValue || "Not set";

  return (
    <div className={`grid grid-cols-[1fr,auto,1fr] gap-4 p-3 rounded-lg ${isDifferent ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/30'}`}>
      <div className="text-sm">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        <p className={isDifferent ? 'text-red-400 line-through' : ''}>{left}</p>
      </div>
      <div className="flex items-center">
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="text-sm">
        <div className="flex items-center gap-2 text-muted-foreground mb-1">
          {icon}
          <span className="text-xs">{label}</span>
        </div>
        <p className={isDifferent ? 'text-green-400 font-medium' : ''}>{right}</p>
      </div>
    </div>
  );
}

export function ContractVersionComparison({ contractId }: ContractVersionComparisonProps) {
  const { data: versions, isLoading } = useContractVersions(contractId);
  const [leftVersionId, setLeftVersionId] = useState<string>("");
  const [rightVersionId, setRightVersionId] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);

  const leftVersion = versions?.find(v => v.id === leftVersionId);
  const rightVersion = versions?.find(v => v.id === rightVersionId);

  if (isLoading) {
    return <Skeleton className="h-10 w-40" />;
  }

  if (!versions || versions.length < 2) {
    return null;
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <GitCompare className="h-4 w-4" />
          Compare Versions
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCompare className="h-5 w-5" />
            Compare Contract Versions
          </DialogTitle>
          <DialogDescription>
            Select two versions to compare their differences side by side
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">From Version</label>
            <Select value={leftVersionId} onValueChange={setLeftVersionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select version..." />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id} disabled={v.id === rightVersionId}>
                    v{v.version_number} - {v.status} ({format(new Date(v.created_at), "MMM d, yyyy")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">To Version</label>
            <Select value={rightVersionId} onValueChange={setRightVersionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select version..." />
              </SelectTrigger>
              <SelectContent>
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id} disabled={v.id === leftVersionId}>
                    v{v.version_number} - {v.status} ({format(new Date(v.created_at), "MMM d, yyyy")})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {leftVersion && rightVersion ? (
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {/* Version Headers */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4">
                <Card className="bg-muted/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Version {leftVersion.version_number}</span>
                      <Badge variant={statusColors[leftVersion.status] || "secondary"}>
                        {leftVersion.status}
                      </Badge>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(leftVersion.created_at), "MMMM d, yyyy 'at' h:mm a")}
                    </p>
                  </CardHeader>
                </Card>
                <div className="flex items-center">
                  <ArrowRight className="h-5 w-5 text-primary" />
                </div>
                <Card className="bg-muted/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span>Version {rightVersion.version_number}</span>
                      <Badge variant={statusColors[rightVersion.status] || "secondary"}>
                        {rightVersion.status}
                      </Badge>
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(rightVersion.created_at), "MMMM d, yyyy 'at' h:mm a")}
                    </p>
                  </CardHeader>
                </Card>
              </div>

              {/* Field Comparisons */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-muted-foreground mb-3">Field Changes</h4>
                
                <ComparisonField
                  label="Title"
                  icon={<FileText className="h-3 w-3" />}
                  leftValue={leftVersion.title}
                  rightValue={rightVersion.title}
                />

                <ComparisonField
                  label="Status"
                  icon={<FileText className="h-3 w-3" />}
                  leftValue={leftVersion.status}
                  rightValue={rightVersion.status}
                />

                <ComparisonField
                  label="Signer Name"
                  icon={<User className="h-3 w-3" />}
                  leftValue={leftVersion.signer_name}
                  rightValue={rightVersion.signer_name}
                />

                <ComparisonField
                  label="Signer Email"
                  icon={<Mail className="h-3 w-3" />}
                  leftValue={leftVersion.signer_email}
                  rightValue={rightVersion.signer_email}
                />

                <ComparisonField
                  label="Expiration Date"
                  icon={<Calendar className="h-3 w-3" />}
                  leftValue={leftVersion.expires_at ? format(new Date(leftVersion.expires_at), "MMM d, yyyy") : null}
                  rightValue={rightVersion.expires_at ? format(new Date(rightVersion.expires_at), "MMM d, yyyy") : null}
                />
              </div>

              {/* Change Summaries */}
              {(leftVersion.change_summary || rightVersion.change_summary) && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Change Notes</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">v{leftVersion.version_number} Notes</p>
                      <p className="text-sm">{leftVersion.change_summary || "No notes"}</p>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">v{rightVersion.version_number} Notes</p>
                      <p className="text-sm">{rightVersion.change_summary || "No notes"}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground">
            <p className="text-sm">Select two versions to compare</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
